import { createApi } from '@reduxjs/toolkit/query/react';
import { Company, CompanyHierarchyNode, CompanyHistoryData } from '../../types/asset.types';
import { createExtendedTokenBaseQuery } from './baseQuery';
import { ApiEndpoints } from '../../services/ApiUrls'; // adjust the path as needed

export interface CompanyPaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Company[];
}

export interface CompanyCreateInput {
  name: string;
  description?: string;
  parentCompany?: string;
  isActive?: boolean;
}

export interface CompanyUpdateInput extends Partial<CompanyCreateInput> {
  updateReason?: string;
}

export interface CompanyFilter {
  search?: string;
  parentCompanyId?: string | null;
  isActive?: boolean;
}

export interface CompanyHierarchyFilter {
  rootCompanyId?: string;
  includeInactive?: boolean;
}

export const companyApi = createApi({
  reducerPath: 'companyApi',
  baseQuery: createExtendedTokenBaseQuery(''),
  tagTypes: ['Company', 'CompanyHierarchy', 'CompanyHistory'],
  endpoints: (builder) => ({
    getCompanies: builder.query<CompanyPaginatedResponse, CompanyFilter | void>({
      query: (filter = {}) => {
        const params = new URLSearchParams();
        if (filter) {
          if (filter.search) params.append('search', filter.search);
          if ('parentCompanyId' in filter) {
            params.append('parent_company', filter.parentCompanyId === null ? 'null' : filter.parentCompanyId || '');
          }
          if (filter.isActive !== undefined) params.append('is_active', filter.isActive.toString());
        }
        return { url: ApiEndpoints.companies.list, params };
      },
      providesTags: ['Company'],
    }),

    getCompanyById: builder.query<Company, string>({
      query: (id) => ApiEndpoints.companies.detail(id),
      providesTags: (result, error, id) => [{ type: 'Company', id }],
    }),

    createCompany: builder.mutation<Company, CompanyCreateInput>({
      query: (company) => ({
        url: ApiEndpoints.companies.list,
        method: 'POST',
        body: company,
      }),
      invalidatesTags: ['Company', 'CompanyHierarchy'],
    }),

    updateCompany: builder.mutation<Company, { id: string; company: CompanyUpdateInput }>({
      query: ({ id, company }) => ({
        url: ApiEndpoints.companies.detail(id),
        method: 'PATCH',
        body: company,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Company', id },
        'Company',
        'CompanyHierarchy',
      ],
    }),

    getCompanyHierarchy: builder.query<CompanyHierarchyNode[], CompanyHierarchyFilter | void>({
      query: (filter = {}) => {
        const params = new URLSearchParams();
        if (filter.rootCompanyId) params.append('rootCompanyId', filter.rootCompanyId);
        if (filter.includeInactive !== undefined) params.append('includeInactive', filter.includeInactive.toString());
        return { url: ApiEndpoints.companies.hierarchy, params };
      },
      providesTags: ['CompanyHierarchy'],
    }),

    getCompanyHistory: builder.query<CompanyHistoryData, string>({
      query: (id) => ApiEndpoints.companies.history(id),
      providesTags: (result, error, id) => [{ type: 'CompanyHistory', id }],
    }),
  }),
  
});

// Export hooks
export const {
  useGetCompaniesQuery,
  useGetCompanyByIdQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useGetCompanyHierarchyQuery,
  useGetCompanyHistoryQuery,
} = companyApi;